import {
  DocumentationOnlyIntegration,
  IntegrationDataFlow,
  IntegrationType,
} from 'studio5/pages/integrations/integrations.types';

export const ACTIVE_CAMPAIGN_ID = 'ACTIVE_CAMPAIGN';
export const AIRTABLE_ID = 'AIRTABLE';
export const AUTOPILOT_JOURNEYS_ID = 'AUTOPILOT_JOURNEYS';
export const CALENDLY_ID = 'CALENDLY';
export const DRIFT_ID = 'DRIFT';
export const FRESHPAINT_ID = 'FRESHPAINT';
export const GOOGLE_SHEETS_ID = 'GOOGLE_SHEETS';
export const HIGHTOUCH_ID = 'HIGHTOUCH';
export const IFTTT_ID = 'IFTTT';
export const LAUNCHDARKLY_ID = 'LAUNCHDARKLY';
export const LOOM_ID = 'LOOM';
export const MAKE_ID = 'MAKE';
export const MICROSOFT_TEAMS_ID = 'MICROSOFT_TEAMS';
export const PAYKICKSTART_ID = 'PAYKICKSTART';
export const PRODPAD_ID = 'PRODPAD';
export const PRODUCTBOARD_ID = 'PRODUCTBOARD';
export const RECURLY_ID = 'RECURLY';
export const STITCH_DATA_ID = 'STITCH_DATA';
export const STRIPE_ID = 'STRIPE';
export const TOPLYNE_ID = 'TOPLYNE';
export const TYPEFORM_ID = 'TYPEFORM';
export const ZAPIER_ID = 'ZAPIER';
export const ZOHO_CRM_ID = 'ZOHO_CRM';
export const CENSUS_ID = 'CENSUS';

export type IntegrationDocumentationOnlyId =
  | typeof ACTIVE_CAMPAIGN_ID
  | typeof AIRTABLE_ID
  | typeof AUTOPILOT_JOURNEYS_ID
  | typeof CALENDLY_ID
  | typeof DRIFT_ID
  | typeof FRESHPAINT_ID
  | typeof GOOGLE_SHEETS_ID
  | typeof HIGHTOUCH_ID
  | typeof IFTTT_ID
  | typeof LAUNCHDARKLY_ID
  | typeof LOOM_ID
  | typeof MAKE_ID
  | typeof MICROSOFT_TEAMS_ID
  | typeof PAYKICKSTART_ID
  | typeof PRODPAD_ID
  | typeof PRODUCTBOARD_ID
  | typeof RECURLY_ID
  | typeof STITCH_DATA_ID
  | typeof STRIPE_ID
  | typeof TOPLYNE_ID
  | typeof TYPEFORM_ID
  | typeof ZAPIER_ID
  | typeof ZOHO_CRM_ID
  | typeof CENSUS_ID;

export const INTEGRATIONS_DOCUMENTATION_ONLY: Record<
  IntegrationDocumentationOnlyId,
  DocumentationOnlyIntegration
> = {
  [ACTIVE_CAMPAIGN_ID]: {
    id: ACTIVE_CAMPAIGN_ID,
    name: 'ActiveCampaign',
    logoAssetUrl: new URL('assets/active-campaign.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction.',
    longDescription: `
    Appcues' ActiveCampaign Integration (through Zapier) makes it easy to automatically target marketing campaigns based on user's interactions.

    - Send emails to users based on their interactions with Appcues experiences (when a user completes or skips flows, checklists, etc.)
    - Follow up with users via email after they provide an in-product NPS score
    - Create a multi-channel onboarding experience with more relevant messaging by triggering emails to users who have taken/failed to take key steps in the product
  `,
  },
  [AIRTABLE_ID]: {
    id: AIRTABLE_ID,
    name: 'Airtable',
    logoAssetUrl: new URL('assets/airtable.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations/airtable',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Collect and organize information to better sort through user interactions and feedback.',
    longDescription: `
    Appcues' Airtable integration (through Zapier) makes it easy to automatically pass information from in-product into Airtable records to sort it in an organized fashion.

    - Organize NPS scores and feedback to better understand user feedback
    - Collect in-app survey submissions in one place to use in other places
    - Create lists of users who complete or fail to complete key actions (e.g. Flows or Checklists)
  `,
  },
  [AUTOPILOT_JOURNEYS_ID]: {
    id: AUTOPILOT_JOURNEYS_ID,
    name: 'Autopilot Journeys',
    logoAssetUrl: new URL('assets/autopilot.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Create an omnichannel marketing strategy by targeting campaigns based on user interaction. ',
    longDescription: `
    Appcues' Autopilot Journeys Integration (through Zapier) makes it easy to automatically target marketing campaigns based on user's interactions.

    - Add users to a list in Autopilot Journeys when they interact with Appcues experiences (user completes or skips  flows, checklists, etc.)
    - Create a list of users in Autopilot Journeys after they submit an NPS score
  `,
  },
  [CALENDLY_ID]: {
    id: CALENDLY_ID,
    name: 'Calendly',
    logoAssetUrl: new URL('assets/calendly.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-use-cases/schedule-user-interviews-calendly',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription: 'Allow users to schedule meetings directly in-product.',
    longDescription: `
    Embed a Calendly calendar in your Appcues experiences. Users will be able to schedule time with your teams more easily.
  `,
  },
  [DRIFT_ID]: {
    id: DRIFT_ID,
    name: 'Drift',
    logoAssetUrl: new URL('assets/drift.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations/drift',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Pass user behavior data from your warehouse directly into Appcues for more relevant in-app experiences.',
    longDescription: `
    Appcues' Census Integration makes it easy to sync user behavior data from your whole tech stack directly to Appcues.

    - Create more personalized and relevant in-app experiences by targeting users who have taken specific actions within other tools
  `,
  },
  [FRESHPAINT_ID]: {
    id: FRESHPAINT_ID,
    name: 'Freshpaint',
    logoAssetUrl: new URL('assets/freshpaint.png', import.meta.url).href,
    documentationUrl:
      'https://documentation.freshpaint.io/destinations/apps/appcues',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Enable the collection and integration of user data across all of your specialized tools.',
    longDescription: `
    Appcues' Freshpaint integration makes it easy to automatically collect user data and pass it to other specialized tools, so your team can make the most of the information you all share.

    - Collect data when users take key actions (which users have/haven't seen, completed, and interacted with flows) and pass them to analytics tools (like Heap, Mixpanel, Amplitude, and Google Analytics) for deeper analysis and insights
    - Continue your user's experience across multiple tools (like your marketing automation platform) to send messages after users take action
    - Give users access to features within your product management tool of choice and pass those properties to Appcues to target those users with in-app experiences describing how to use the feature
  `,
  },
  [GOOGLE_SHEETS_ID]: {
    id: GOOGLE_SHEETS_ID,
    name: 'Google Sheets',
    logoAssetUrl: new URL('assets/google-sheets.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-use-cases/send-appcues-survey-responses-to-google-sheets-using-webhooks',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Collect and organize information to better sort through user interactions and feedback.',
    longDescription: `
    Appcues' Google Sheets integration (through Zapier) makes it easy to automatically pass information from in-product directly to Google Sheets to sort it in an organized fashion.

    - Organize NPS scores and feedback to better understand user feedback
    - Collect in-app survey submissions in one place to use in other places
    - Create lists of users who complete or fail to complete key actions (e.g. Flows or Checklists)
  `,
  },
  [HIGHTOUCH_ID]: {
    id: HIGHTOUCH_ID,
    name: 'Hightouch',
    logoAssetUrl: new URL('assets/hightouch.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/2077599-untitled-article?draft=true',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Pass user behavior data from your warehouse directly into Appcues for more relevant in-app experiences.',
    longDescription: `
    Appcues' Hightouch Integration makes it easy to sync user behavior data from your whole tech stack directly to Appcues.

    - Create more personalized and relevant in-app experiences by targeting users who have taken specific actions within other tools
  `,
  },
  [IFTTT_ID]: {
    id: IFTTT_ID,
    name: 'IFTTT',
    logoAssetUrl: new URL('assets/ifttt.png', import.meta.url).href,
    documentationUrl: 'https://docs.appcues.com/webhooks-management',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Reduce manual tasks by creating automated workflows with other tools.',
    longDescription: `
    Appcues' IFTTT Integration (through webhooks) makes it easy to enable your product stack to talk to each other like one big, happy, product-led family. Use the integration to connect the things that happen in your product to a plethera of other tools so the conversation flows–automatically.

    There are hundreds of ways to automate workflows between Appcues and the tools in your tech stack. Some of our favorites include:
    - Push changes in user information directly to Salesforce, HubSpot, or another CRM
    - Follow up on user actions and NPS feedback by sending emails through HubSpot, Customer.io, Mailchimp, and more
    - Send NPS data into Slack, Microsoft Teams, or other messaging tools to increase the visibility of user feedback and empower teams to take action faster
    - Pass in-app survey and NPS submissions into Airtable, Google Sheets, and other databases to sort and organize feedback
    - When a user’s payment fails, trigger an experience in Appcues
    - Alert your sales team when customers make in-app requests for pricing and feature information
  `,
  },
  [LAUNCHDARKLY_ID]: {
    id: LAUNCHDARKLY_ID,
    name: 'LaunchDarkly',
    logoAssetUrl: new URL('assets/launchdarkly.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-use-cases/increase-feature-adoption-launchdarkly-segment',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Enable feature access for users based on their interaction with in-product experiences.',
    longDescription: `
    Appcues' LaunchDarkly Integration (through Zapier) makes it easy to create a connection between in-product experiences and feature access.

    - Enable a feature flag in LaunchDarkly when they interact when an Appcues experience allowing users to opt themselves into beta features
  `,
  },
  [LOOM_ID]: {
    id: LOOM_ID,
    name: 'Loom',
    logoAssetUrl: new URL('assets/loom.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-use-cases/personalize-trialer-onboarding-loom',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Automatically send videos to every trialer from their opportunity owner, right inside your product, by embedding Loom videos in your Appcues experiences.',
    longDescription:
      'Automatically send videos to every trialer from their opportunity owner, right inside your product, by embedding Loom videos in your Appcues experiences.',
  },
  [MAKE_ID]: {
    id: MAKE_ID,
    name: 'Make',
    logoAssetUrl: new URL('assets/make.png', import.meta.url).href,
    documentationUrl: 'https://docs.appcues.com/webhooks-management',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Reduce manual tasks by creating automated workflows with other tools.',
    longDescription: `
    Appcues' Make Integration (through webhooks) makes it easy to enable your product stack to talk to each other like one big, happy, product-led family. Use the integration to connect the things that happen in your product to a plethera of other tools so the conversation flows–automatically.

    There are hundreds of ways to automate workflows between Appcues and the tools in your tech stack. Some of our favorites include:
    - Push changes in user information directly to Salesforce, HubSpot, or another CRM
    - Follow up on user actions and NPS feedback by sending emails through HubSpot, Customer.io, Mailchimp, and more
    - Send NPS data into Slack, Microsoft Teams, or other messaging tools to increase the visibility of user feedback and empower teams to take action faster
    - Pass in-app survey and NPS submissions into Airtable, Google Sheets, and other databases to sort and organize feedback
    - When a user’s payment fails, trigger an experience in Appcues
    - Alert your sales team when customers make in-app requests for pricing and feature information
  `,
  },
  [MICROSOFT_TEAMS_ID]: {
    id: MICROSOFT_TEAMS_ID,
    name: 'Microsoft Teams',
    logoAssetUrl: new URL('assets/microsoft-teams.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Increase visibility and action with real time updates on user interactions right to your preferred channels. ',
    longDescription: `
    Appcues' Microsoft Teams integration (through Zapier) makes it easy to create a customer-centric organization by surfacing real-time user feedback and information in the places your teams are already in. Encourage teams to take action on what they see for immediate user follow-up.

    Automatically send details to a designated Microsoft Teams channel, including:
    - NPS Scores and feedback
    - Form Submission content
    - Completed action (like a Flow or Checklist completion)
  `,
  },
  [PAYKICKSTART_ID]: {
    id: PAYKICKSTART_ID,
    name: 'PayKickstart',
    logoAssetUrl: new URL('assets/paykickstart.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Automatically surface in-app experiences based on purchase behavior.',
    longDescription: `
    Appcues' PayKickstart Integration (through Zapier) makes it easy to launch in-product experiences based on a customer's interactions.

    Pass an event to Appcues to target in-app experiences to specific users when:
    - A payment fails
    - A user abandons their cart
    - A new trial has started or ended
    - A subscription has been added or changed
  `,
  },
  [PRODPAD_ID]: {
    id: PRODPAD_ID,
    name: 'ProdPad',
    logoAssetUrl: new URL('assets/prodpad.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations/prodpad',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Collect and organize user feedback to better understand it and take action.',
    longDescription: `
    Appcues' ProdPad integration (through Zapier) makes it easy to pass product feedback collected in-app directly to ProductPad to better understand and organize it.

    - Create feedback in ProdPad after a user submits an NPS score and feedback
    - Collect in-app survey submissions in one place to understand user and product feedback
  `,
  },
  [PRODUCTBOARD_ID]: {
    id: PRODUCTBOARD_ID,
    name: 'Productboard',
    logoAssetUrl: new URL('assets/productboard.png', import.meta.url).href,
    documentationUrl:
      'https://zapier.com/apps/appcues/integrations/productboard',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Collect and organize user feedback to better understand it and take action.',
    longDescription: `
    Appcues' ProdPad integration (through Zapier) makes it easy to pass product feedback collected in-app directly to ProductPad to better understand and organize it.

    - Create feedback in ProdPad after a user submits an NPS score and feedback
    - Collect in-app survey submissions in one place to understand user and product feedback
  `,
  },
  [RECURLY_ID]: {
    id: RECURLY_ID,
    name: 'Recurly',
    logoAssetUrl: new URL('assets/recurly.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Automatically surface in-app experiences based on subscription and payment changes. ',
    longDescription: `
    Appcues Recurly Integration (through Zapier) makes it easy to launch in-product experiences based on subscription and payment changes.

    - Pass an event to Appcues when a payment fails in Recurly, allowing you to target an in-app experience to those users, encouraging them to update their payment method
    - Pass an event to Appcues when a new payment has been made, a new invoice has been added, or a new subscription has been added, allowing you to target an in-app experience to those users
  `,
  },
  [STITCH_DATA_ID]: {
    id: STITCH_DATA_ID,
    name: 'Stitch Data',
    logoAssetUrl: new URL('assets/stitch.png', import.meta.url).href,
    documentationUrl:
      'https://www.stitchdata.com/docs/integrations/webhooks/stitch-incoming-webhooks',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Add Appcues experience interaction data to your data warehouse using Stitch Data.',
    longDescription:
      'Add Appcues experience interaction data to your data warehouse using Stitch Data.',
  },
  [STRIPE_ID]: {
    id: STRIPE_ID,
    name: 'Stripe',
    logoAssetUrl: new URL('assets/stripe.png', import.meta.url).href,
    documentationUrl: 'https://zapier.com/apps/appcues/integrations',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Automatically surface in-app experiences based on subscription and payment changes. ',
    longDescription: `
    Appcues Stripe Integration (through Zapier) makes it easy to launch in-product experiences based on subscription and payment changes.

    - Pass an event or profile attributes to Appcues when a payment fails in Stripe, allowing you to target an in-app experience to those users, encouraging them to update their payment method
    - Pass an event or profile attributes to Appcues when a new payment has been made, a new invoice has been added, or a new subscription has been added, allowing you to target an in-app experience to those users
  `,
  },
  [TOPLYNE_ID]: {
    id: TOPLYNE_ID,
    name: 'Toplyne',
    logoAssetUrl: new URL('assets/toplyne.png', import.meta.url).href,
    documentationUrl: 'https://www.toplyne.io/integrations/appcues',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      "Send Toplyne's churn risk and upsell opportunity flags to Appcues for targeting and Segmentation.",
    longDescription: `
    Appcues' Toplyne integration allows for Toplyne's AI-modeled churn and upsell flags to be sent to Appcues for targeting and segmentation.
  `,
  },
  [TYPEFORM_ID]: {
    id: TYPEFORM_ID,
    name: 'Typeform',
    logoAssetUrl: new URL('assets/typeform.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/user-experiences-use-cases/typeform-survey-flow',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Gain feedback and measure sentiment by surveying users directly in product.',
    longDescription: `
    Appcues' Typeform Integration (through Zapier) makes it easy to

    -
    - Pass an event to Appcues after a user has submitted a Typeform to target them with an in-app experience
  `,
  },
  [ZAPIER_ID]: {
    id: ZAPIER_ID,
    name: 'Zapier',
    logoAssetUrl: new URL('assets/zapier.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/using-appcues-and-zapier',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.TWO_WAY,
    shortDescription:
      'Reduce manual tasks by creating automated workflows with over 5,000 apps.',
    longDescription: `
    Appcues' Zapier integration makes it easy to enable your product stack to talk to each other like one big, happy, product-led family. Use the integration to connect the things that happen in your product to 5,000+ apps so the conversation flows–automatically.

    There are hundreds of ways to automate workflows between Appcues and the tools in your tech stack. Some of our favorites include:
    - Push changes in user information directly to Salesforce, HubSpot, or another CRM
    - Follow up on user actions and NPS feedback by sending emails through HubSpot, Customer.io, Mailchimp, and more
    - Send NPS data into Slack, Microsoft Teams, or other messaging tools to increase the visibility of user feedback and empower teams to take action faster
    - Pass in-app survey and NPS submissions into Airtable, Google Sheets, and other databases to sort and organize feedback
    - When a user’s payment fails, trigger an experience in Appcues
    - Alert your sales team when customers make in-app requests for pricing and feature information
  `,
  },
  [ZOHO_CRM_ID]: {
    id: ZOHO_CRM_ID,
    name: 'Zoho CRM',
    logoAssetUrl: new URL('assets/zoho-crm.png', import.meta.url).href,
    documentationUrl: 'https://docs.appcues.com/webhooks-management',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.OUTBOUND,
    shortDescription:
      'Automatically pass user properties for better targeting and enable teams to see how users are interacting with in-product experiences.',
    longDescription: `
    Appcues' Zoho CRM Integration (through webhooks) enables you to deliver more personalized and effective in-product experiences and see how users are interacting with in-product experiences.

    Let sales and service teams see what prospects are doing in-product by updating contacts in Zoho CRM when users skip, complete, and interact with in-product experiences (flows, checklists, etc.)
    Automatically pass user NPS scores and survey feedback to Zoho CRM contacts to trigger additional follow up
  `,
  },
  [CENSUS_ID]: {
    id: CENSUS_ID,
    name: 'Census',
    logoAssetUrl: new URL('assets/census.png', import.meta.url).href,
    documentationUrl:
      'https://docs.appcues.com/en_US/integration-documents/census-integration',
    isPremium: false,
    integrationType: IntegrationType.DOCUMENTATION_ONLY,
    dataFlowDirection: IntegrationDataFlow.INBOUND,
    shortDescription:
      'Pass user behavior data from your warehouse directly into Appcues for more relevant in-app experiences.',
    longDescription: `
    "Appcues' Census Integration makes it easy to sync user behavior data from your whole tech stack directly to Appcues.

    - Create more personalized and relevant in-app experiences by targeting users who have taken specific actions within other tools"
  `,
  },
};
